import React, { useState } from 'react';
import { useApiData, useAuthenticatedFetch } from '../../api/client';
import { type ProjectMembership, type DastarkhanUser, type PaginatedResponse } from '../../api/dastarkhan';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Table,
  Button,
  Checkbox,
  Modal,
  Select,
  Card,
  Stack,
  Flex,
  useTheme,
} from '@fti/design-system';

interface ProjectMembersProps {
  projectId: string;
}

export const ProjectMembers: React.FC<ProjectMembersProps> = ({ projectId }) => {
  const { theme } = useTheme();
  const { isAdmin } = usePermissions();
  const membersEndpoint = `/dastarkhan/projects/${projectId}/members`;
  const { data: members, loading, error: fetchError, refetch: fetchMembers } = useApiData<ProjectMembership[]>(membersEndpoint);
  const { fetchData } = useAuthenticatedFetch();
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [allUsers, setAllUsers] = useState<DastarkhanUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const displayError = error || (fetchError ? 'Failed to load members' : null);

  const handleOpenAdd = async () => {
    setShowAddModal(true);
    try {
      const result = await fetchData('/dastarkhan/users?page=1&per_page=200') as PaginatedResponse<DastarkhanUser> | undefined;
      if (result) {
        const memberIds = new Set((members || []).map((m) => m.user_id));
        setAllUsers(result.items.filter((u) => !memberIds.has(u.id)));
      }
    } catch {
      setError('Failed to load users');
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;
    setAddLoading(true);
    try {
      await fetchData(`/dastarkhan/projects/${projectId}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_id: selectedUserId }),
      });
      setShowAddModal(false);
      setSelectedUserId('');
      fetchMembers();
    } catch {
      setError('Failed to add member');
    } finally {
      setAddLoading(false);
    }
  };

  const handlePermissionChange = async (userId: string, perm: 'can_update' | 'can_delete', value: boolean) => {
    try {
      await fetchData(`/dastarkhan/projects/${projectId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ [perm]: value }),
      });
      fetchMembers();
    } catch {
      setError('Failed to update permissions');
    }
  };

  const handleRemove = async (userId: string) => {
    if (!confirm('Remove this member from the project?')) return;
    try {
      await fetchData(`/dastarkhan/projects/${projectId}/members/${userId}`, {
        method: 'DELETE',
      });
      fetchMembers();
    } catch {
      setError('Failed to remove member');
    }
  };

  return (
    <Stack gap="md">
      <Flex justify="between" align="center">
        <h3 style={{ margin: 0, fontSize: '1rem', color: theme.colors.text.primary }}>
          Project Members ({(members || []).length})
        </h3>
        {isAdmin && (
          <Button variant="solid" color="primary" size="sm" onClick={handleOpenAdd}>
            Add Member
          </Button>
        )}
      </Flex>

      {displayError && <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>{displayError}</div>}

      <Table
        data={(members || []) as unknown as Record<string, unknown>[]}
        loading={loading}
        emptyMessage="No members"
        columns={[
          { key: 'user_email', header: 'Email' },
          { key: 'user_display_name', header: 'Name' },
          {
            key: 'can_read',
            header: 'Read',
            render: (_value: unknown, row: Record<string, unknown>) => {
              const m = row as unknown as ProjectMembership;
              return <Checkbox checked={m.can_read} disabled />;
            },
          },
          {
            key: 'can_create',
            header: 'Create',
            render: (_value: unknown, row: Record<string, unknown>) => {
              const m = row as unknown as ProjectMembership;
              return <Checkbox checked={m.can_create} disabled />;
            },
          },
          {
            key: 'can_update',
            header: 'Update',
            render: (_value: unknown, row: Record<string, unknown>) => {
              const m = row as unknown as ProjectMembership;
              return (
                <Checkbox
                  checked={m.can_update}
                  disabled={!isAdmin}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange(m.user_id, 'can_update', e.target.checked)}
                />
              );
            },
          },
          {
            key: 'can_delete',
            header: 'Delete',
            render: (_value: unknown, row: Record<string, unknown>) => {
              const m = row as unknown as ProjectMembership;
              return (
                <Checkbox
                  checked={m.can_delete}
                  disabled={!isAdmin}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePermissionChange(m.user_id, 'can_delete', e.target.checked)}
                />
              );
            },
          },
          ...(isAdmin ? [{
            key: 'user_id',
            header: 'Actions',
            render: (_value: unknown, row: Record<string, unknown>) => {
              const m = row as unknown as ProjectMembership;
              return (
                <Button variant="outline" color="error" size="sm" onClick={() => handleRemove(m.user_id)}>
                  Remove
                </Button>
              );
            },
          }] : []),
        ]}
      />

      {showAddModal && (
        <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)}>
          <Modal.Header onClose={() => setShowAddModal(false)}>Add Member</Modal.Header>
          <Modal.Body>
            <Stack gap="md">
              <Select
                label="User"
                placeholder="Select a user..."
                value={selectedUserId}
                onChange={(value: string) => setSelectedUserId(value)}
                options={allUsers.map((u) => ({
                  value: u.id,
                  label: `${u.email} (${u.display_name})`,
                }))}
              />
              <p style={{ margin: 0, fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                New members get Read + Create permissions by default.
              </p>
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <Flex justify="end" gap="sm">
              <Button variant="outline" size="sm" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button variant="solid" color="primary" size="sm" onClick={handleAddMember} disabled={!selectedUserId || addLoading}>
                {addLoading ? 'Adding...' : 'Add Member'}
              </Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}
    </Stack>
  );
};
